import { useState, useEffect, useCallback } from 'react';
import { 
  ArrowLeft, Mic, Volume2, Activity, Shield, 
  CheckCircle, XCircle, Loader2, RefreshCw,
  Zap, Fingerprint, Radio, Lock, ListTodo
} from 'lucide-react';
import { Button } from '@zaogig/ui/button';
import { Card, CardContent } from '@zaogig/ui/card';
import { Progress } from '@zaogig/ui/progress';
import { Badge } from '@zaogig/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@zaogig/ui/tabs';
import { useVoiceRecorder } from '../hooks/use-voice';
import { apiClient } from '@zaogig/auth';

import { toast } from 'sonner';
import TaskAttendance from '../components/TaskAttendance';

interface AttendancePageProps {
  onComplete: (success: boolean) => void;
  onBack: () => void;
}

type Stage = 'intro' | 'noise-detect' | 'challenge' | 'recording' | 'processing' | 'success' | 'failed';

interface ProcessingStep {
  name: string;
  description: string;
  icon: React.ReactNode;
  status: 'pending' | 'processing' | 'completed';
}

export default function AttendancePage({ onComplete, onBack }: AttendancePageProps) {
  const [activeTab, setActiveTab] = useState<'task' | 'voice'>('task');
  const [stage, setStage] = useState<Stage>('intro');
  const [noiseLevel, setNoiseLevel] = useState(0);
  const [challenge, setChallenge] = useState('');
  const [confidence, setConfidence] = useState(0);
  const [processingSteps, setProcessingSteps] = useState<ProcessingStep[]>([
    { name: '物理降噪', description: '分离稳态噪音', icon: <Volume2 className="w-5 h-5" />, status: 'pending' },
    { name: '声源聚焦', description: '锁定最近人声', icon: <Radio className="w-5 h-5" />, status: 'pending' },
    { name: '特征提取', description: '提取声道指纹', icon: <Fingerprint className="w-5 h-5" />, status: 'pending' },
    { name: '活体检测', description: '验证真人发声', icon: <Shield className="w-5 h-5" />, status: 'pending' },
    { name: '声纹比对', description: '匹配数据库', icon: <Lock className="w-5 h-5" />, status: 'pending' },
  ]);

  // 使用录音Hook
  const { 
    recordingTime, 
    startRecording, 
    resetRecording
  } = useVoiceRecorder({
    maxDuration: 3000,
    onComplete: (blob) => {
      // 录音完成，开始处理
      handleRecordingComplete(blob);
    },
    onError: (error) => {
      toast.error(error.message);
      setStage('failed');
    }
  });

  // 生成随机挑战码
  const generateChallenge = useCallback(() => {
    const digits = '0123456789';
    let result = '';
    for (let i = 0; i < 4; i++) {
      result += digits[Math.floor(Math.random() * 10)];
      if (i < 3) result += '-';
    }
    return result;
  }, []);

  // 开始环境噪声检测
  const startNoiseDetection = useCallback(() => {
    setStage('noise-detect');
    setNoiseLevel(0);
    
    // 模拟噪声检测（实际项目中这里应该使用音频分析）
    const interval = setInterval(() => {
      setNoiseLevel(prev => {
        const newLevel = prev + Math.random() * 15;
        if (newLevel >= 65) {
          clearInterval(interval);
          setTimeout(() => {
            setChallenge(generateChallenge());
            setStage('challenge');
          }, 500);
          return 65;
        }
        return newLevel;
      });
    }, 200);
  }, [generateChallenge]);

  // 开始录音
  const handleStartRecording = useCallback(async () => {
    try {
      await startRecording();
      setStage('recording');
    } catch (error) {
      toast.error('无法启动录音，请检查麦克风权限');
    }
  }, [startRecording]);

  // 录音完成处理
  const handleRecordingComplete = (blob: Blob) => {
    // 进入处理阶段
    setStage('processing');
    startProcessing(blob);
  };

  // 模拟处理流程
  const startProcessing = useCallback((_blob: Blob) => {
    let stepIndex = 0;
    
    const processStep = () => {
      if (stepIndex >= processingSteps.length) {
        // 处理完成，模拟成功率
        const success = Math.random() > 0.1; // 90%成功率
        const finalConfidence = Math.floor(92 + Math.random() * 6);
        setConfidence(finalConfidence);
        
        setTimeout(() => {
          if (success) {
            setStage('success');
            // 添加到考勤记录
            
            apiClient.post('/attendance/check-in', {
              // id: Date.now().toString(),
              // userId: 'u001',
              date: new Date().toISOString().split('T')[0],
              checkInTime: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' }),
              status: 'normal'
            });
          } else {
            setStage('failed');
          }
        }, 500);
        return;
      }
      
      setProcessingSteps(prev => prev.map((step, idx) => ({
        ...step,
        status: idx === stepIndex ? 'processing' : idx < stepIndex ? 'completed' : 'pending'
      })));
      
      setTimeout(() => {
        setProcessingSteps(prev => prev.map((step, idx) => ({
          ...step,
          status: idx === stepIndex ? 'completed' : step.status
        })));
        stepIndex++;
        setTimeout(processStep, 600);
      }, 800);
    };
    
    processStep();
  }, [processingSteps.length]);

  // 重试
  const handleRetry = useCallback(() => {
    setStage('intro');
    setNoiseLevel(0);
    setConfidence(0);
    setProcessingSteps(prev => prev.map(s => ({ ...s, status: 'pending' })));
    resetRecording();
  }, [resetRecording]);

  // 清理
  useEffect(() => {
    return () => {
      resetRecording();
    };
  }, [resetRecording]);

  // 渲染不同阶段的内容
  const renderContent = () => {
    switch (stage) {
      case 'intro':
        return <IntroStage onStart={startNoiseDetection} />;
      case 'noise-detect':
        return <NoiseDetectStage noiseLevel={noiseLevel} />;
      case 'challenge':
        return (
          <ChallengeStage 
            challenge={challenge} 
            noiseLevel={noiseLevel}
            onStartRecording={handleStartRecording}
          />
        );
      case 'recording':
        return <RecordingStage challenge={challenge} recordingTime={recordingTime} />;
      case 'processing':
        return <ProcessingStage steps={processingSteps} />;
      case 'success':
        return (
          <SuccessStage 
            confidence={confidence}
            noiseLevel={noiseLevel}
            onComplete={() => onComplete(true)}
          />
        );
      case 'failed':
        return (
          <FailedStage 
            onRetry={handleRetry}
            onBack={() => onComplete(false)}
          />
        );
    }
  };

  // 如果选择任务型考勤，直接显示任务考勤组件
  if (activeTab === 'task') {
    return (
      <TaskAttendance
        shiftId={undefined}
        jobId={undefined}
        onBack={onBack}
        onComplete={onComplete}
      />
    );
  }

  // 否则显示声纹考勤
  return (
    <div className="wx-page bg-white">
      {/* 顶部导航 */}
      <div className="flex items-center gap-3 px-4 py-4 border-b border-gray-100">
        <button 
          onClick={onBack}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <h1 className="text-lg font-semibold text-gray-800">考勤打卡</h1>
      </div>
      
      {/* 标签页切换 */}
      <Tabs defaultValue="voice" className="w-full" onValueChange={(v) => setActiveTab(v as any)}>
        <div className="px-4 pt-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="task" className="flex items-center gap-2">
              <ListTodo className="w-4 h-4" />
              任务型
            </TabsTrigger>
            <TabsTrigger value="voice" className="flex items-center gap-2">
              <Mic className="w-4 h-4" />
              声纹
            </TabsTrigger>
          </TabsList>
        </div>
        
        <TabsContent value="voice" className="mt-0">
          {renderContent()}
        </TabsContent>
      </Tabs>
    </div>
  );
}

// 各阶段组件

function IntroStage({ onStart }: { onStart: () => void }) {
  const features = [
    { title: '物理特征分离', desc: '自动抠除机器稳态噪音' },
    { title: '特定数据增强训练', desc: 'AI在噪音环境中训练' },
    { title: '声道指纹提取', desc: '识别生理特征而非音色' },
    { title: '动态密码活体检测', desc: '防止录音作弊' },
  ];

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-6">
      <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mb-6">
        <Mic className="w-12 h-12 text-blue-600" />
      </div>
      <h2 className="text-xl font-bold text-gray-800 mb-2">声纹考勤打卡</h2>
      <p className="text-gray-500 text-center mb-8">
        使用工业级声纹识别技术<br />嘈杂环境也能精准打卡
      </p>
      
      <Card className="w-full mb-6">
        <CardContent className="p-4">
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <Zap className="w-5 h-5 text-blue-500" />
            四层抗噪技术
          </h3>
          <div className="space-y-3">
            {features.map((item, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                  {idx + 1}
                </div>
                <div>
                  <p className="font-medium text-gray-800 text-sm">{item.title}</p>
                  <p className="text-xs text-gray-500">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      
      <Button 
        onClick={onStart}
        className="wx-btn-primary w-full"
      >
        <Mic className="w-5 h-5 mr-2" />
        开始声纹打卡
      </Button>
    </div>
  );
}

function NoiseDetectStage({ noiseLevel }: { noiseLevel: number }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-6">
      <div className="relative mb-8">
        <div className="w-32 h-32 bg-blue-100 rounded-full flex items-center justify-center">
          <Activity className="w-16 h-16 text-blue-600" />
        </div>
        <div className="absolute inset-0 bg-blue-500 rounded-full animate-ping opacity-20" />
      </div>
      
      <h2 className="text-xl font-bold text-gray-800 mb-2">环境噪声检测中</h2>
      <p className="text-gray-500 text-center mb-6">
        正在分析背景噪音特征<br />生成降噪指纹...
      </p>
      
      <div className="w-full max-w-xs">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-500">噪声级别</span>
          <span className="text-lg font-bold text-blue-600">{noiseLevel.toFixed(0)}dB</span>
        </div>
        <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-green-500 via-yellow-500 to-red-500 transition-all duration-200"
            style={{ width: `${(noiseLevel / 100) * 100}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-400 mt-1">
          <span>安静</span>
          <span>嘈杂</span>
          <span>极吵</span>
        </div>
      </div>
      
      <div className="mt-8 flex items-center gap-2 text-sm text-blue-600">
        <Loader2 className="w-4 h-4 animate-spin" />
        正在分离人声与噪音...
      </div>
    </div>
  );
}

function ChallengeStage({ 
  challenge, 
  noiseLevel,
  onStartRecording 
}: { 
  challenge: string; 
  noiseLevel: number;
  onStartRecording: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-6">
      <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mb-6">
        <Shield className="w-10 h-10 text-orange-600" />
      </div>
      
      <h2 className="text-xl font-bold text-gray-800 mb-2">动态密码验证</h2>
      <p className="text-gray-500 text-center mb-6">
        请在3秒内朗读以下数字<br />防止录音作弊
      </p>
      
      <div className="bg-gray-900 text-white px-8 py-6 rounded-2xl mb-8">
        <p className="text-4xl font-mono font-bold tracking-wider">
          {challenge}
        </p>
      </div>
      
      <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
        <Volume2 className="w-4 h-4" />
        <span>环境噪声: {noiseLevel.toFixed(0)}dB</span>
      </div>
      
      <Button 
        onClick={onStartRecording}
        className="wx-btn-primary w-full"
      >
        <Mic className="w-5 h-5 mr-2" />
        开始朗读
      </Button>
    </div>
  );
}

function RecordingStage({ 
  challenge, 
  recordingTime 
}: { 
  challenge: string; 
  recordingTime: number;
}) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-6">
      <div className="relative mb-8">
        <div className="w-32 h-32 bg-red-100 rounded-full flex items-center justify-center">
          <Mic className="w-16 h-16 text-red-600" />
        </div>
        <div className="absolute inset-0 bg-red-500 rounded-full animate-pulse opacity-30" />
      </div>
      
      <h2 className="text-xl font-bold text-gray-800 mb-2">正在录音</h2>
      <p className="text-gray-500 text-center mb-6">请朗读：{challenge}</p>
      
      {/* 声波动画 */}
      <div className="flex items-center gap-1 h-16 mb-6">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="w-1.5 bg-blue-500 rounded-full sound-wave-bar"
            style={{
              height: `${28 + ((i * 17) % 52)}%`,
              animationDelay: `${i * 0.05}s`
            }}
          />
        ))}
      </div>
      
      <div className="w-full max-w-xs">
        <Progress value={(recordingTime / 3) * 100} className="h-2" />
        <p className="text-center text-sm text-gray-500 mt-2">
          {recordingTime.toFixed(1)}s / 3.0s
        </p>
      </div>
    </div>
  );
}

function ProcessingStage({ steps }: { steps: ProcessingStep[] }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-6">
      <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mb-6">
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
      </div>
      
      <h2 className="text-xl font-bold text-gray-800 mb-2">声纹识别中</h2>
      <p className="text-gray-500 text-center mb-8">
        四层抗噪技术正在处理...
      </p>
      
      <div className="w-full space-y-3">
        {steps.map((step, idx) => (
          <div 
            key={idx}
            className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
              step.status === 'processing' ? 'bg-blue-50 border border-blue-200' :
              step.status === 'completed' ? 'bg-green-50' : 'bg-gray-50'
            }`}
          >
            <div className={`transition-colors ${
              step.status === 'processing' ? 'text-blue-600' :
              step.status === 'completed' ? 'text-green-600' : 'text-gray-400'
            }`}>
              {step.status === 'completed' ? <CheckCircle className="w-5 h-5" /> : step.icon}
            </div>
            <div className="flex-1">
              <p className={`font-medium text-sm ${
                step.status === 'pending' ? 'text-gray-400' : 'text-gray-800'
              }`}>
                {step.name}
              </p>
              <p className="text-xs text-gray-500">{step.description}</p>
            </div>
            {step.status === 'processing' && (
              <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function SuccessStage({ 
  confidence, 
  noiseLevel,
  onComplete 
}: { 
  confidence: number; 
  noiseLevel: number;
  onComplete: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-6">
      <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mb-6">
        <CheckCircle className="w-12 h-12 text-green-600" />
      </div>
      
      <h2 className="text-xl font-bold text-gray-800 mb-2">考勤成功！</h2>
      <p className="text-gray-500 text-center mb-6">
        声纹识别通过<br />
        {new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })}
      </p>
      
      <Card className="w-full mb-6">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-gray-500">识别置信度</span>
            <Badge className="wx-badge-green">{confidence}%</Badge>
          </div>
          <div className="flex items-center justify-between mb-3">
            <span className="text-gray-500">环境噪声</span>
            <span className="font-medium">{noiseLevel.toFixed(0)}dB</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-500">抗噪等级</span>
            <div className="flex items-center gap-1 text-orange-500">
              <Zap className="w-4 h-4" />
              <span className="font-medium">工业级</span>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Button 
        onClick={onComplete}
        className="wx-btn-primary w-full"
      >
        完成
      </Button>
    </div>
  );
}

function FailedStage({ 
  onRetry, 
  onBack 
}: { 
  onRetry: () => void;
  onBack: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-6">
      <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mb-6">
        <XCircle className="w-12 h-12 text-red-600" />
      </div>
      
      <h2 className="text-xl font-bold text-gray-800 mb-2">识别失败</h2>
      <p className="text-gray-500 text-center mb-6">
        可能是环境太吵或朗读不清晰<br />
        请重试或换个安静的环境
      </p>
      
      <div className="flex gap-3 w-full">
        <Button 
          onClick={onRetry}
          variant="outline"
          className="flex-1"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          重试
        </Button>
        <Button 
          onClick={onBack}
          className="wx-btn-primary flex-1"
        >
          返回
        </Button>
      </div>
    </div>
  );
}
